/**
 * The Billing-Console card kit — the five primitives every console form on OptiHub is built
 * from, in one place.
 *
 * Extracted from `BillingConsole.tsx` (which is 5 000 lines and growing) when Apply Coupon
 * Code became a second console that needed the identical chrome. Nothing here changed in the
 * move: same markup, same classes, same testids, so the Billing Console's five test suites
 * exercise exactly what they did before.
 *
 * The ONE addition is `ActionButtons`' `testIdPrefix`. Its testids used to be hardcoded
 * `billing-<keyId>-submit`; they now default to that and can be re-prefixed by a caller that
 * is not the Billing Console. A default rather than a required argument on purpose — a
 * required one would have meant touching every existing call site, which is exactly the kind
 * of churn that makes a pure move stop being reviewable as one.
 */
import React from 'react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';

export type Msg = { kind: 'ok' | 'err'; text: string } | null;

export function ResultLine({ msg, testId }: { msg: Msg; testId: string }) {
  if (!msg) return null;
  return (
    <p
      className={msg.kind === 'ok' ? 'text-xs text-muted-foreground' : 'text-xs text-destructive'}
      data-testid={testId}
    >
      {msg.text}
    </p>
  );
}

export interface CardShellProps {
  testId: string;
  title: string;
  description: string;
  children: React.ReactNode;
}
export function CardShell({ testId, title, description, children }: CardShellProps) {
  return (
    <div
      className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
      data-testid={testId}
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {children}
    </div>
  );
}

export function LabeledInput(props: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  helper?: string;
  type?: 'text' | 'date' | 'email';
  numeric?: boolean;
  min?: string;
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={props.id} className="text-xs">
        {props.label}
      </Label>
      <Input
        id={props.id}
        type={props.type === 'date' ? 'date' : 'text'}
        value={props.value}
        min={props.type === 'date' ? props.min : undefined}
        onChange={(e) =>
          props.onChange(props.numeric ? e.target.value.replace(/[^0-9]/g, '') : e.target.value)
        }
        placeholder={props.placeholder}
        data-testid={props.testId}
      />
      {props.helper ? <p className="text-[11px] text-muted-foreground">{props.helper}</p> : null}
    </div>
  );
}

export function LabeledSelect(props: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: { label: string; value: string }[];
  helper?: string;
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs">{props.label}</Label>
      <Select value={props.value} onValueChange={props.onChange}>
        <SelectTrigger className="cursor-pointer" data-testid={props.testId}>
          <SelectValue placeholder={props.placeholder} />
        </SelectTrigger>
        <SelectContent>
          {props.options.map((o) => (
            <SelectItem key={o.value} value={o.value} className="cursor-pointer">
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {props.helper ? <p className="text-[11px] text-muted-foreground">{props.helper}</p> : null}
    </div>
  );
}

export function ActionButtons({
  keyId,
  submitLabel,
  valid,
  busy,
  onSubmit,
  onClear,
  testIdPrefix = 'billing',
}: {
  keyId: string;
  submitLabel: string;
  valid: boolean;
  busy: boolean;
  onSubmit: () => void;
  onClear: () => void;
  /** Testid namespace. Defaults to the Billing Console's, so existing callers are unchanged. */
  testIdPrefix?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <Button
        onClick={onSubmit}
        disabled={!valid || busy}
        className="cursor-pointer"
        data-testid={`${testIdPrefix}-${keyId}-submit`}
      >
        {busy ? 'Working...' : submitLabel}
      </Button>
      <Button
        onClick={onClear}
        disabled={busy}
        variant="ghost"
        className="cursor-pointer"
        data-testid={`${testIdPrefix}-${keyId}-clear`}
      >
        {'Clear'}
      </Button>
    </div>
  );
}

